import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import {
  notificationsAreSupportedInThisContext,
  readBrowserNotificationsUserEnabled,
} from '@/notifications/browserNotificationSettings';
import { syncWebPushSubscriptionWithServer } from '@/notifications/webPushSubscribe';
import './BrowserNotificationPrompt.css';

/** «Позже» скрывает плашку до закрытия вкладки — при следующем визите запрос снова можно показать. */
const SNOOZE_KEY = 'tracker_notifications_prompt_snoozed';

function readSnoozed(): boolean {
  try {
    return sessionStorage.getItem(SNOOZE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSnoozed(): void {
  try {
    sessionStorage.setItem(SNOOZE_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Универсальная плашка запроса разрешения на браузерные уведомления (без системного диалога до клика).
 */
export function BrowserNotificationPrompt() {
  const { isAuthenticated } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });
  const [snoozed, setSnoozed] = useState(readSnoozed);
  /** Только длительная синхронизация push после granted — не до requestPermission. */
  const [pushSyncBusy, setPushSyncBusy] = useState(false);

  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    setPermission(Notification.permission);
  }, [isAuthenticated]);

  const onEnable = useCallback(() => {
    if (typeof Notification === 'undefined') return;

    if (Notification.permission === 'granted') {
      setPushSyncBusy(true);
      void syncWebPushSubscriptionWithServer().finally(() => setPushSyncBusy(false));
      return;
    }
    if (Notification.permission === 'denied') {
      setPermission('denied');
      return;
    }

    /**
     * Критично: не вызывать setState до Notification.requestPermission() в цепочке клика.
     * Иначе Chrome/Edge часто не показывают системный диалог.
     */
    Notification.requestPermission()
      .then((next) => {
        setPermission(next);
        if (next === 'granted') {
          setPushSyncBusy(true);
          return syncWebPushSubscriptionWithServer().finally(() => setPushSyncBusy(false));
        }
      })
      .catch(() => {
        setPermission(Notification.permission);
      });
  }, []);

  const onLater = useCallback(() => {
    writeSnoozed();
    setSnoozed(true);
  }, []);

  if (!isAuthenticated) return null;
  if (permission === 'unsupported') return null;
  if (!readBrowserNotificationsUserEnabled()) return null;
  if (permission === 'granted') return null;
  if (snoozed) return null;

  if (!notificationsAreSupportedInThisContext()) {
    return (
      <aside className="browserNotificationPrompt" role="status" aria-live="polite">
        <p className="browserNotificationPrompt__title">Невозможно включить уведомления</p>
        <p className="browserNotificationPrompt__text">
          К сожалению, уведомления не поддерживаются в этом браузере.
        </p>
        <div className="browserNotificationPrompt__actions">
          <button type="button" className="browserNotificationPrompt__btnGhost" onClick={onLater}>
            Понятно
          </button>
        </div>
      </aside>
    );
  }

  if (permission === 'denied') {
    return (
      <aside
        className="browserNotificationPrompt"
        role="status"
        aria-live="polite"
      >
        <p className="browserNotificationPrompt__title">Уведомления выключены</p>
        <p className="browserNotificationPrompt__text">
          Браузер блокирует уведомления для этого сайта. Включите их в настройках сайта (значок замка
          или «i» в адресной строке), затем обновите страницу.
        </p>
        <div className="browserNotificationPrompt__actions">
          <button
            type="button"
            className="browserNotificationPrompt__btnGhost"
            onClick={onLater}
          >
            Скрыть
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="browserNotificationPrompt" role="dialog" aria-labelledby="notif-prompt-title">
      <p id="notif-prompt-title" className="browserNotificationPrompt__title">
        Уведомления
      </p>
      <p className="browserNotificationPrompt__text">
        Включите уведомления — сможем сообщать о важных событиях, когда вы на другой вкладке или свернули
        окно. На телефоне это работает, если сайт добавлен на главный экран.
      </p>
      <div className="browserNotificationPrompt__actions">
        <button
          type="button"
          className="browserNotificationPrompt__btnPrimary"
          onClick={onEnable}
          disabled={pushSyncBusy}
        >
          {pushSyncBusy ? 'Сохраняем…' : 'Включить уведомления'}
        </button>
        <button
          type="button"
          className="browserNotificationPrompt__btnGhost"
          onClick={onLater}
          disabled={pushSyncBusy}
        >
          Позже
        </button>
      </div>
    </aside>
  );
}
